import React, { useState, useEffect, useCallback } from 'react';
import { useListContext, useTranslate } from 'react-admin';
import { Box, FormGroup, FormControl, Typography, Button, useTheme, useMediaQuery, TextField } from '@mui/material';
import Popover from '@mui/material/Popover';
import { styled } from '@mui/material/styles';
import { Palette } from '@/themes/themeOverrides/Palette';
import { breakpoints } from '@/themes/themeOverrides/Breakpoints';
import { isEqual } from 'lodash';
import { DateInput, isDateAfterToday } from '@/components/global/inputs/DateInput';
import { parseDateValue, toDotDateString } from '@/helpers/date/dateValue';
import CustomTimePicker from '@/components/global/inputs/TimePicker.jsx';
import PropTypes from 'prop-types';

const StyledFormGroup = styled(FormGroup)(() => ({
    '&.from-to-filter-group': {
        borderRadius: '12px',
        padding: '13px 45px 13px 16px',
        border: `2px solid ${Palette.gray.gray200}`,
        cursor: 'pointer',
        fontSize: '14px',
        fontStyle: 'normal',
        fontWeight: '400',
        lineHeight: '20px',
        letterSpacing: '-0.14px',
        color: Palette.black,
        height: '48px',
        minWidth: '200px',
        position: 'relative',
        [`@media (max-width: ${breakpoints.values.md}px)`]: {
            top: 'auto',
            margin: '4px 0',
            fontSize: '16px',
        },
        '&.active': {
            borderColor: Palette.black,
            outline: `2px solid ${Palette.yellow.yellow200}`,
            '& + .MuiPopover-root.from-to-filter': {
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    display: 'block',
                },
            },
        },
        '& + .MuiPopover-root.from-to-filter': {
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                display: 'none',
            },
        },
    },
    '& .from-to-filter-input': {
        display: 'flex',
        flexDirection: 'row',
        '& .MuiTypography-caption': {
            backgroundColor: Palette.gray.gray75,
            fontSize: '11px',
            fontStyle: 'normal',
            fontWeight: '400',
            lineHeight: '16px',
            letterSpacing: '-0.11px',
            position: 'absolute',
            top: '-10px',
            left: '10px',
            zIndex: 1,
            transition: 'none',
            whiteSpace: 'nowrap',
            padding: '0 4px',
            color: Palette.gray.gray600,
            [`@media (max-width: ${breakpoints.values.md}px)`]: {
                backgroundColor: Palette.white,
                color: Palette.black,
            },
        },
        '&.empty:not(.active)': {
            lineHeight: '20px',
            display: 'block',
            '& .MuiTypography-caption': {
                fontSize: '14px',
                lineHeight: '20px',
                display: 'block',
                position: 'static',
                color: Palette.black,
                padding: '0',
                [`@media (max-width: ${breakpoints.values.md}px)`]: {
                    fontSize: '16px',
                },
            },
        },
        '&.empty.active': {},
        '&.active': {
            borderColor: Palette.black,
            '& .MuiTypography-caption': {
                lineHeight: '16px',
            },
        },
    },
}));

const ALLOWED_INPUT_TYPES = ['number', 'percent', 'date', 'time'];

const hasRangeBoundValue = value => value !== '' && value != null;

/** Returns > 0 when from is greater than to, < 0 when less, 0 when equal/incomparable. */
const compareRangeValues = (from, to, type) => {
    if (type === 'date') {
        const fromDate = parseDateValue(from);
        const toDate = parseDateValue(to);
        if (!fromDate || !toDate) return 0;
        const fromTime = new Date(fromDate.getFullYear(), fromDate.getMonth(), fromDate.getDate()).getTime();
        const toTime = new Date(toDate.getFullYear(), toDate.getMonth(), toDate.getDate()).getTime();
        return fromTime - toTime;
    }

    if (type === 'time') {
        return String(from).localeCompare(String(to));
    }

    const fromNum = Number(from);
    const toNum = Number(to);
    if (Number.isNaN(fromNum) || Number.isNaN(toNum)) return 0;
    return fromNum - toNum;
};

const isFromGreaterThanTo = (from, to, type) => hasRangeBoundValue(from) && hasRangeBoundValue(to) && compareRangeValues(from, to, type) > 0;

const FromToFilterComponent = ({ source, label, type = 'number', allowFutureDates = false }) => {
    const translate = useTranslate();
    const { filterValues, setFilters } = useListContext();
    const maxDate = type === 'date' && !allowFutureDates ? 'today' : null;

    // Source musi kończyć się na _range
    if (!source.endsWith('_range')) {
        throw new Error('FromToFilterComponent: source must end with _range');
    }

    // Wyciągnij wartości z tablicy [from, to]
    const [fromValue = '', toValue = ''] = filterValues?.[source] || ['', ''];

    const [anchorEl, setAnchorEl] = useState(null);
    const [pendingFromValue, setPendingFromValue] = useState(fromValue);
    const [pendingToValue, setPendingToValue] = useState(toValue);
    const [isFromValid, setIsFromValid] = useState(true);
    const [isToValid, setIsToValid] = useState(true);

    // Generuj nazwy tylko dla kluczy wewnętrznych
    const baseSource = source.slice(0, -6);
    const fromSource = `${baseSource}_gte`;
    const toSource = `${baseSource}_lte`;

    const hasInvalidDateInput =
        type === 'date' && (!isFromValid || !isToValid || (!allowFutureDates && (isDateAfterToday(pendingFromValue) || isDateAfterToday(pendingToValue))));
    const hasInvalidRange = isFromGreaterThanTo(pendingFromValue, pendingToValue, type);
    const hasInvalidInput = hasInvalidDateInput || hasInvalidRange;
    const hasUnchangedValues = isEqual(pendingFromValue, fromValue) && isEqual(pendingToValue, toValue);

    const handleFromValueChange = useCallback(
        value => {
            setPendingFromValue(value);
            // Gdy „Od” > „Do”, automatycznie zaktualizuj „Do” do wartości „Od”
            if (isFromGreaterThanTo(value, pendingToValue, type)) {
                setPendingToValue(value);
            }
        },
        [pendingToValue, type],
    );

    const handleToValueChange = useCallback(value => {
        setPendingToValue(value);
    }, []);

    const handleApply = () => {
        if (hasInvalidInput) {
            return;
        }

        const updatedFilters = { ...filterValues };

        // Zawsze używaj tablicy [from, to]
        if (hasRangeBoundValue(pendingFromValue) || hasRangeBoundValue(pendingToValue)) {
            updatedFilters[source] = [pendingFromValue ?? '', pendingToValue ?? ''];
        } else {
            delete updatedFilters[source];
        }

        setFilters(updatedFilters, false);
        setAnchorEl(null);
    };

    const handleCancel = useCallback(() => {
        setPendingFromValue(fromValue);
        setPendingToValue(toValue);
        setIsFromValid(true);
        setIsToValid(true);
        setAnchorEl(null);
    }, [fromValue, toValue]);

    const open = Boolean(anchorEl);
    const isEmpty = !hasRangeBoundValue(fromValue) && !hasRangeBoundValue(toValue);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const handleClick = event => {
        const id = `${fromSource}-${toSource}`;
        if (isMobile) {
            event.stopPropagation();
            if (open) {
                handleCancel();
            } else {
                setPendingFromValue(fromValue);
                setPendingToValue(toValue);
                setAnchorEl(event.currentTarget);
                document.dispatchEvent(new CustomEvent('fromToFilterOpened', { detail: id }));
            }
        } else {
            setPendingFromValue(fromValue);
            setPendingToValue(toValue);
            setAnchorEl(event.currentTarget);
            document.dispatchEvent(new CustomEvent('fromToFilterOpened', { detail: id }));
        }
    };

    useEffect(() => {
        setPendingFromValue(fromValue);
        setPendingToValue(toValue);
        setIsFromValid(true);
        setIsToValid(true);
    }, [fromValue, toValue]);

    useEffect(() => {
        if (open) {
            document.body.style.setProperty('overflow', 'visible', 'important');
            document.body.style.removeProperty('padding-right');
        } else {
            document.body.style.removeProperty('overflow');
        }

        return () => {
            document.body.style.removeProperty('overflow');
        };
    }, [open]);

    useEffect(() => {
        const id = `${fromSource}-${toSource}`;
        const handleOtherOpen = event => {
            if (open && event?.detail !== id) {
                handleCancel();
            }
        };
        document.addEventListener('fromToFilterOpened', handleOtherOpen);
        return () => {
            document.removeEventListener('fromToFilterOpened', handleOtherOpen);
        };
    }, [open, fromSource, toSource, handleCancel]);

    const formatDateDisplayValue = value => {
        const date = parseDateValue(value);
        return date ? toDotDateString(date) : value;
    };

    const getDisplayValue = () => {
        if (!hasRangeBoundValue(fromValue) && !hasRangeBoundValue(toValue)) return '';
        if (type === 'percent') {
            const fromDisplay = hasRangeBoundValue(fromValue) ? `${Number((fromValue * 100).toFixed(2))}%` : '';
            const toDisplay = hasRangeBoundValue(toValue) ? `${Number((toValue * 100).toFixed(2))}%` : '';
            return `${fromDisplay} - ${toDisplay}`;
        }
        if (type === 'date') {
            const fromDisplay = hasRangeBoundValue(fromValue) ? formatDateDisplayValue(fromValue) : '';
            const toDisplay = hasRangeBoundValue(toValue) ? formatDateDisplayValue(toValue) : '';
            return `${fromDisplay} - ${toDisplay}`;
        }
        return `${fromValue} - ${toValue}`;
    };

    return (
        <>
            <StyledFormGroup
                key={`${fromSource}-${toSource}`}
                source={source}
                label={label}
                className={`from-to-filter-group${open ? ' active' : ''}`}
                onClick={handleClick}
            >
                <Box className={`from-to-filter-input${open ? ' active' : ''}${isEmpty ? ' empty' : ''}`}>
                    <Typography variant='caption'>{label}</Typography>
                    {getDisplayValue()}
                </Box>
            </StyledFormGroup>
            <Popover
                open={open}
                anchorEl={anchorEl}
                onClose={handleCancel}
                className={`from-to-filter from-to-filter--${type}`}
                anchorOrigin={{
                    vertical: 'bottom',
                    horizontal: 'left',
                }}
                disablePortal={isMobile}
            >
                <Box className={`from-to-filter-popup from-to-filter-popup--${type}`}>
                    <Typography variant='h6'>{label}</Typography>
                    <Box className='inputs'>
                        <FromToInput
                            source={fromSource}
                            label={translate('common.filter.from')}
                            type={type}
                            pendingValue={pendingFromValue}
                            setPendingValue={handleFromValueChange}
                            maxDate={maxDate}
                            onValidityChange={setIsFromValid}
                            error={hasInvalidRange}
                        />
                        <FromToInput
                            source={toSource}
                            label={translate('common.filter.to')}
                            type={type}
                            pendingValue={pendingToValue}
                            setPendingValue={handleToValueChange}
                            maxDate={maxDate}
                            onValidityChange={setIsToValid}
                            error={hasInvalidRange}
                        />
                    </Box>
                    {hasInvalidRange && (
                        <Typography variant='caption' color='error' sx={{ display: 'block', px: '24px', pb: '8px', marginTop: '-10px', fontSize: '11px' }}>
                            {translate('common.filter.rangeInvalid')}
                        </Typography>
                    )}
                    <Box className='actions'>
                        <Button
                            type='button'
                            variant='contained'
                            color='primary'
                            fullWidth
                            onClick={handleApply}
                            disabled={hasInvalidInput || hasUnchangedValues}
                        >
                            {translate('common.apply')}
                        </Button>
                    </Box>
                </Box>
            </Popover>
        </>
    );
};

const FromToInput = ({ source, label, type, pendingValue, setPendingValue, maxDate, onValidityChange, error = false }) => {
    let inputValue = '';
    if (hasRangeBoundValue(pendingValue)) {
        inputValue = type === 'percent' ? Number((pendingValue * 100).toFixed(2)) : pendingValue;
    }

    return (
        <FormControl error={error}>
            {type === 'time' && (
                <Box className='time-picker' sx={{ position: 'relative' }}>
                    <CustomTimePicker key={source} label={label} value={pendingValue || ''} onChange={value => setPendingValue(value)} error={error} />
                </Box>
            )}
            {type === 'date' && (
                <DateInput
                    key={source}
                    label={label}
                    value={pendingValue || ''}
                    onChange={value => setPendingValue(value)}
                    onValidityChange={onValidityChange}
                    className={error ? 'date-picker--error' : undefined}
                    {...(maxDate ? { maxDate } : {})}
                />
            )}
            {(type === 'number' || type === 'percent') && (
                <TextField
                    key={source}
                    label={label}
                    type='number'
                    value={inputValue}
                    error={error}
                    onChange={event => {
                        const value = event.target.value;
                        if (type === 'percent') {
                            setPendingValue(value ? value / 100 : '');
                        } else {
                            setPendingValue(value);
                        }
                    }}
                    fullWidth
                    variant='outlined'
                    {...(type === 'percent'
                        ? {
                              format: value => (typeof value === 'number' ? Number((value * 100).toFixed(2)) : value),
                              parse: value => (typeof value === 'number' ? value / 100 : value),
                              inputProps: {
                                  step: 0.01,
                                  min: 0,
                              },
                          }
                        : {})}
                />
            )}
        </FormControl>
    );
};

FromToFilterComponent.propTypes = {
    source: PropTypes.string.isRequired, // musi kończyć się na _range (np. 'updatedAt_range')
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(ALLOWED_INPUT_TYPES),
    allowFutureDates: PropTypes.bool,
};

FromToInput.propTypes = {
    source: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    type: PropTypes.oneOf(ALLOWED_INPUT_TYPES).isRequired,
    pendingValue: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    setPendingValue: PropTypes.func.isRequired,
    maxDate: PropTypes.oneOf(['today']),
    onValidityChange: PropTypes.func,
    error: PropTypes.bool,
};

export default FromToFilterComponent;
